import { BookMarked } from "lucide-react";
import { Panel } from "@/components/dashboard/Panel";
import { useModuleFailedCount } from "@/hooks/useStudent";
import { isExamSession } from "@/lib/usage";
import { L } from "@/i18n";

const PASS = 75;

const Stat = ({ label, value, cls = "text-slate-100", testId }) => (
  <div className="rounded-md bg-[#0f172a] p-3">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-1 font-mono text-xl font-bold ${cls}`} data-testid={testId}>{value}</p>
  </div>
);

const summarize = (sessions, module) => {
  const list = (sessions ?? []).filter((s) => !s.locked && (s.module === module || s.module_title?.startsWith(`${module}.`) || s.module_title === module));
  const exams = list.filter(isExamSession);
  const sum = (arr, k) => arr.reduce((n, s) => n + (s[k] ?? 0), 0);
  return {
    attempts: list.length,
    correct: sum(list, "score"),
    answered: sum(list, "total"),
    exams: exams.length,
    examsPassed: exams.filter((s) => s.percentage >= PASS).length,
    examCorrect: sum(exams, "score"),
    examTotal: sum(exams, "total"),
  };
};

export const LastModuleStats = ({ user, sessionsQuery }) => {
  const latest = (sessionsQuery.data ?? []).find((x) => !x.locked);
  const module = user.last_module ?? latest?.module;
  const submodule = user.last_module ? user.last_submodule : latest?.module_title;
  const failed = useModuleFailedCount(user.id, module);
  const hasSessions = sessionsQuery.isSuccess;
  const s = summarize(sessionsQuery.data, module);
  const pct = s.answered ? Math.round((s.correct / s.answered) * 100) : null;
  const dash = "—";

  return (
    <Panel
      testId="student-last-module"
      title={L("Last module studied", "Último módulo estudado")}
      subtitle={module ? [module, submodule !== module ? submodule : null].filter(Boolean).join(" · ") : L("No study activity recorded yet.", "Ainda sem atividade de estudo registada.")}
    >
      {!module ? (
        <BookMarked className="mx-auto h-6 w-6 text-slate-600" />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label={L("Exams done", "Exames feitos")} value={hasSessions ? s.exams : dash} testId="last-module-exams" />
            <Stat label={L("Exams passed", "Exames aprovados")} value={hasSessions ? `${s.examsPassed}/${s.exams}` : dash} cls="text-emerald-400" testId="last-module-exams-passed" />
            <Stat label={L("Correct answers", "Respostas certas")} value={hasSessions ? `${s.correct}/${s.answered}` : dash} cls="text-amber-400" testId="last-module-correct" />
            <Stat label={L("Failed questions", "Perguntas falhadas")} value={failed.data ?? dash} cls="text-rose-400" testId="last-module-failed-questions" />
          </div>
          {hasSessions && pct != null && (
            <p className="mt-3 text-xs text-slate-400" data-testid="last-module-summary">
              {L("{a} attempts · {p}% correct overall · exams: {ec}/{et} correct", "{a} tentativas · {p}% certas no total · exames: {ec}/{et} certas", { a: s.attempts, p: pct, ec: s.examCorrect, et: s.examTotal })}
            </p>
          )}
          {!hasSessions && !sessionsQuery.isLoading && (
            <p className="mt-3 text-xs text-slate-500" data-testid="last-module-sessions-unavailable">
              {L("Quiz/exam history for this student is not provided by the API yet (GET /api/admin/users/{id}/sessions).", "O histórico de quizzes/exames deste aluno ainda não é fornecido pela API (GET /api/admin/users/{id}/sessions).")}
            </p>
          )}
        </>
      )}
    </Panel>
  );
};
